import { Checkbox, Box } from '@mui/material';
import { isBooleanValueTrue, normalizeBooleanish } from '../../../utils';
import type { CellContext, Table } from '@tanstack/react-table';
import type { ColumnMetadata, LookupDataItem } from '../../../types';
import { EditableCell } from '../EditableCell';
import { ValidationErrorTooltip } from '../ui';
import type { Config } from 'dompurify';
import DOMPurify from 'dompurify';
import {
  isScreenshotColumn,
  isFileColumn,
  isDateColumn,
  isNumberColumn,
  isBooleanColumn,
  isListColumn,
  isLookupColumn,
  isColorColumn,
  getAppMediaImagePaths,
  getAppMediaImageTextHtml,
} from '../utils/columnUtils';
import { ImageCell } from './ImageCell';
import { AppMediaImageCell } from './AppMediaImageCell';
import { FileCell } from './FileCell';
import { renderGridCellTemplate } from '../../../cell-templates';
import { formatGridCellDisplayValue } from '../utils/gridCellDisplayValue';
import { CellTextWithTooltip } from './CellTextWithTooltip';
import { MultiLineEditorCell } from './MultiLineEditorCell';
import { isValidColor, renderGridEditCellTemplate } from '../../../cell-edit-templates';

type DataGridCellProps<T> = {
  context: CellContext<T, unknown>;
  columnMetadataMap: Map<string, ColumnMetadata>;
};

const SANITIZE_OPTIONS: Config = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'svg', 'path'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'd', 'viewBox', 'fill'],
  FORBID_ATTR: ['style'],
};

const isValidUrl = (value: string): boolean => {
  if (!value) return false;

  try {
    const parsedUrl = new URL(value);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

const lookupItemPassesDefaultFilters = (
  item: LookupDataItem | undefined,
  filters: ColumnMetadata['defaultFilters']
): boolean => {
  if (!item || !filters?.length) return true;

  return filters.every((filter) => {
    const columnName = filter.ColumnName ?? filter.columnName;
    const operator = (filter.Operator ?? filter.operator ?? '=').toLowerCase();
    if (!columnName || !['=', '=='].includes(operator)) return true;

    const field = item.Fields.find((candidate) => candidate.Name === columnName);
    const actual = field?.StringValue ?? (item as unknown as Record<string, unknown>)[columnName];
    const expected = filter.Value ?? filter.value;
    if (actual === undefined) return false;
    return normalizeBooleanish(String(actual)) === normalizeBooleanish(String(expected ?? ''));
  });
};

/**
 * Default cell renderer for DataGrid
 * Handles boolean, date/datetime, image, lookup, and text values
 * Delegates to EditableCell for editable columns
 */
const DataGridCellComponent = <T extends Record<string, unknown>>({
  context,
  columnMetadataMap,
}: DataGridCellProps<T>) => {
  'use memo';
  const { getValue, row, column, table } = context;
  const initialValue = getValue();
  const columnMeta = columnMetadataMap.get(column.id);
  const gridInlineEditable = table.options.meta?.inlineEditable ?? false;
  const wordWrap = table.options.meta?.wordWrap ?? false;
  const isMultiLineEditorPopup =
    columnMeta?.cellTemplateFunction?.toLowerCase() === 'multilineeditorpopup';
  const columnEditable = columnMeta?.editable || columnMeta?.allowEditing;
  const dataType = columnMeta?.type ?? columnMeta?.dataType;
  const isDateTimeColumn = dataType?.toLowerCase() === 'datetime';
  const rowId = String((row.original as Record<string, unknown>).Id || '');
  const isNewRow = rowId.startsWith('temp_');
  const canEditSavedRows = table.options.meta?.canEditSavedRows ?? true;
  const rowPermission = isNewRow ? columnMeta?.canCreate : columnMeta?.canEdit;
  const canEditThisField =
    typeof rowPermission === 'boolean' ? rowPermission : Boolean(columnEditable);
  const canEditThisRow = (isNewRow || canEditSavedRows) && canEditThisField;

  const isEditable =
    gridInlineEditable &&
    columnEditable &&
    canEditThisRow &&
    !isMultiLineEditorPopup &&
    !isListColumn(columnMeta);

  // Lookup map for this column
  const tableLookupMaps = table.options.meta?.lookupMaps;
  const tableLookupOptionMaps = table.options.meta?.lookupOptionMaps;
  const fieldName = columnMeta?.dataField || columnMeta?.field || column.id;
  const lookupMap = tableLookupMaps?.get(fieldName);
  const lookupOptionMap = tableLookupOptionMaps?.get(fieldName) ?? lookupMap;
  const rawLookupItems = table.options.meta?.rawLookupItems?.get(fieldName);
  const lookupItem = rawLookupItems?.find((item) => String(item.Id) === String(initialValue));
  const isFilteredLookupValue = !lookupItemPassesDefaultFilters(
    lookupItem,
    columnMeta?.defaultFilters
  );

  // Check for validation error
  const validationErrors = table.options.meta?.validationErrors;
  const cellKey = `${rowId}:${column.id}`;
  const validationError = validationErrors?.get(cellKey);

  const formattedDisplayContent = formatGridCellDisplayValue({
    value: initialValue,
    lookupMap,
    dataType,
    format: columnMeta?.format,
    dateTimeMode: isDateTimeColumn ? table.options.meta?.dateTimeMode : undefined,
  });
  const lookupDisplayField = columnMeta?.additionalData?.displayExpr;
  const lookupDisplayValue = lookupDisplayField
    ? lookupItem?.Fields.find((field) => field.Name === lookupDisplayField)?.StringValue
    : lookupItem?.DisplayText;
  const displayContent =
    formattedDisplayContent || (lookupItem ? lookupDisplayValue || String(initialValue ?? '') : '');

  if (isFileColumn(columnMeta) && !isEditable) {
    const fileGuid =
      initialValue != null && String(initialValue) !== '' ? String(initialValue) : null;
    return (
      <FileCell
        fileGuid={fileGuid}
        isEditing={isEditable}
        metadata={columnMeta}
        onValueChange={(value) => table.options.meta?.updateData(row.index, column.id, value)}
      />
    );
  }

  // This legacy template is a read-only cell that opens its editor on click,
  // including when the surrounding grid is in inline-edit mode.
  if (isMultiLineEditorPopup) {
    return (
      <MultiLineEditorCell
        value={initialValue}
        rowIndex={row.index}
        columnId={column.id}
        table={table as Table<Record<string, unknown>>}
      />
    );
  }

  // Image rendering — thumbnail + click-to-expand lightbox. Checked before
  // the editable branch since image columns aren't inline-editable inputs.
  if (isScreenshotColumn(columnMeta)) {
    const orig = row.original as Record<string, unknown>;
    const screenshotGuid =
      (orig.PathToScreenshot != null && String(orig.PathToScreenshot) !== ''
        ? String(orig.PathToScreenshot)
        : null) ??
      (orig.ScreenshotPath != null && String(orig.ScreenshotPath) !== ''
        ? String(orig.ScreenshotPath)
        : null);
    const title = orig.WindowTitle != null ? String(orig.WindowTitle) : 'Screenshot';
    return <ImageCell screenshotGuid={screenshotGuid} title={title} />;
  }

  const appMediaImagePaths = getAppMediaImagePaths(initialValue, columnMeta);
  if (appMediaImagePaths.length) {
    const appMediaImageTextHtml = DOMPurify.sanitize(
      getAppMediaImageTextHtml(initialValue),
      SANITIZE_OPTIONS
    );
    return (
      <AppMediaImageCell
        mediaPaths={appMediaImagePaths}
        textHtml={appMediaImageTextHtml || undefined}
      />
    );
  }

  // If editable, delegate to EditableCell
  if (isEditable) {
    const editTemplate = columnMeta
      ? renderGridEditCellTemplate({
          value: initialValue,
          columnMetadata: columnMeta,
          lookupMap: lookupOptionMap,
          displayLookupMap: lookupMap,
          isInvalidLookupValue: isFilteredLookupValue,
          nodeKey: table.options.meta?.nodeKey,
          fieldKey: columnMeta.key,
          baseFilter: table.options.meta?.dropdownBaseFilter,
          parentId: table.options.meta?.parentId,
          onValueChange: (value) => table.options.meta?.updateData(row.index, column.id, value),
          rowData: row.original as Record<string, unknown>,
          onRowValueChange: (patch) => table.options.meta?.updateRowData(row.index, patch),
        })
      : null;
    const editableCell = editTemplate ?? (
      <EditableCell
        initialValue={initialValue}
        rowIndex={row.index}
        columnId={column.id}
        columnMetadata={columnMeta}
        table={table as Table<Record<string, unknown>>}
        rowId={rowId}
        rowData={row.original as Record<string, unknown>}
      />
    );

    const cellInner = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          minWidth: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {editableCell}
      </Box>
    );

    const skipTooltipWrapper =
      isDateColumn(columnMeta) ||
      isNumberColumn(columnMeta) ||
      isBooleanColumn(columnMeta) ||
      isListColumn(columnMeta) ||
      isLookupColumn(columnMeta) ||
      editTemplate !== null;

    const displayContent = formatGridCellDisplayValue({
      value: initialValue,
      lookupMap,
      dataType,
      format: columnMeta?.format,
    });

    const cellWrapper = skipTooltipWrapper ? (
      <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>{cellInner}</Box>
    ) : (
      <CellTextWithTooltip rawText={displayContent || String(initialValue ?? '')}>
        <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>{cellInner}</Box>
      </CellTextWithTooltip>
    );

    if (validationError) {
      return <ValidationErrorTooltip error={validationError}>{cellWrapper}</ValidationErrorTooltip>;
    }

    return cellWrapper;
  }

  const cellTemplateResult = renderGridCellTemplate(columnMeta?.cellTemplateFunction, {
    value: initialValue,
    displayValue: displayContent,
    lookupMap,
    rawLookupItems,
    rawParams: columnMeta?.cellTemplateFunctionParams,
    row: row.original as Record<string, unknown>,
    parentId: table.options.meta?.parentId,
  });

  // If template explicitly hides the cell (e.g. IsDeleted == true → transparent), return null
  if (cellTemplateResult && cellTemplateResult.content === null) {
    return null;
  }

  // Render color dot before display value when column has hasColor + colorColumnName
  const hasColor = columnMeta?.hasColor === true;
  const colorColumnName = columnMeta?.colorColumnName as string | undefined;
  if (hasColor && colorColumnName) {
    const color = String((row.original as Record<string, unknown>)[colorColumnName] ?? '');
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
        {color && (
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>
          <CellTextWithTooltip rawText={displayContent} wordWrap={wordWrap} />
        </Box>
      </Box>
    );
  }

  if (cellTemplateResult) {
    return (
      <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>
        {cellTemplateResult.content}
      </Box>
    );
  }

  if (isColorColumn(columnMeta)) {
    const isValid = isValidColor(initialValue);
    const color = isValid ? String(initialValue) : '';
    return (
      <Box
        aria-label={isValid ? color : 'No color selected'}
        component="span"
        role="img"
        sx={{
          width: 20,
          height: 20,
          display: 'block',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0.5,
          backgroundColor: isValid ? color : 'transparent',
        }}
      />
    );
  }

  if (isListColumn(columnMeta)) {
    let listItems: unknown;
    try {
      listItems = typeof initialValue === 'string' ? JSON.parse(initialValue) : initialValue;
    } catch {
      listItems = undefined;
    }

    if (Array.isArray(listItems)) {
      const items = listItems.map((item) => String(item));
      if (items.length === 1) {
        return <CellTextWithTooltip rawText={items[0]} wordWrap={wordWrap} />;
      }
      if (items.length > 1) {
        return (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {items.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </Box>
        );
      }
    }
  }

  if (typeof initialValue === 'boolean' || isBooleanColumn(columnMeta)) {
    return (
      <Box
        data-grid-centered-control="true"
        sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
      >
        <Checkbox checked={isBooleanValueTrue(initialValue)} disabled size="small" />
      </Box>
    );
  }

  if (columnMeta?.isUrl === true && typeof initialValue === 'string') {
    const url = initialValue.trim();
    if (isValidUrl(url)) {
      return (
        <CellTextWithTooltip rawText={displayContent} wordWrap={wordWrap}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {displayContent}
          </a>
        </CellTextWithTooltip>
      );
    }
  }

  const sanitized = DOMPurify.sanitize(displayContent, SANITIZE_OPTIONS);

  if (wordWrap && sanitized) {
    return (
      <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>
        <Box
          component="span"
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            height: 'auto',
            overflow: 'hidden',
            whiteSpace: 'normal',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            textOverflow: 'ellipsis',
            '& p, & div': {
              display: 'block',
              margin: 0,
              whiteSpace: 'inherit',
            },
            '& br': {
              display: 'block',
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ color: isFilteredLookupValue ? 'error.main' : 'inherit' }}>
      <CellTextWithTooltip rawText={displayContent} sanitizedHtml={sanitized} wordWrap={wordWrap} />
    </Box>
  );
};

export const DataGridCell = DataGridCellComponent;
